import { format, formatDistanceToNow, parseISO, isValid } from "date-fns";

export const STATUS_META = {
  new: { label: "New", tone: "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30" },
  call_back: { label: "Call Back", tone: "bg-violet-500/15 text-violet-700 dark:text-violet-400 border-violet-500/30" },
  no_response: { label: "No Response", tone: "bg-orange-500/15 text-orange-700 dark:text-orange-400 border-orange-500/30" },
  no_requirement: { label: "No Requirement", tone: "bg-neutral-500/15 text-neutral-600 dark:text-neutral-400 border-neutral-500/30" },
  requirement_fulfilled: { label: "Requirement Fulfilled", tone: "bg-teal-500/15 text-teal-700 dark:text-teal-400 border-teal-500/30" },
  sample: { label: "Sample", tone: "bg-cyan-500/15 text-cyan-700 dark:text-cyan-400 border-cyan-500/30" },
  invalid_number: { label: "Invalid Number", tone: "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30" },
  connected: { label: "Connected", tone: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30" },
  won: { label: "Won", tone: "bg-emerald-600/25 text-emerald-800 dark:text-emerald-200 border-emerald-600/50" },
  lost: { label: "Lost", tone: "bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30" },
};

export const STATUS_KEYS = Object.keys(STATUS_META);

export function fmtDate(iso, patt = "dd MMM yyyy") {
  if (!iso) return "—";
  try {
    const d = typeof iso === "string" ? parseISO(iso) : iso;
    if (!isValid(d)) return "—";
    return format(d, patt);
  } catch {
    return "—";
  }
}
export function fmtDateTime(iso) {
  return fmtDate(iso, "dd MMM, HH:mm");
}
export function fromNow(iso) {
  if (!iso) return "—";
  try {
    const d = typeof iso === "string" ? parseISO(iso) : iso;
    return formatDistanceToNow(d, { addSuffix: true });
  } catch { return "—"; }
}

// Clean phone number (only digits + leading +)
export function normalizePhone(phone) {
  if (!phone) return "";
  const trimmed = String(phone).trim();
  const plus = trimmed.startsWith("+") ? "+" : "";
  return plus + trimmed.replace(/[^\d]/g, "");
}

// Normalize a phone number for a WhatsApp `wa.me` link — wa.me requires
// an international-format number WITHOUT the leading "+".
// Handles common Indian input patterns (leading trunk-prefix 0, bare 10-digit).
export function phoneForWhatsapp(phone, defaultCountry = "91") {
  if (!phone) return "";
  const trimmed = String(phone).trim();
  // Already international (e.g. "+91 85114 63200")
  if (trimmed.startsWith("+")) return trimmed.replace(/\D/g, "");
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return "";
  // Indian trunk prefix — e.g. "08511463200" (0 + 10 digits) → 91 + 10 digits
  if (digits.startsWith("0") && digits.length === 11) return defaultCountry + digits.slice(1);
  // Bare 10-digit local number (very common India paste) → prepend country code
  if (digits.length === 10) return defaultCountry + digits;
  // Otherwise trust the caller (already has a country code embedded)
  return digits;
}

export function telLink(phone) {
  return `tel:${normalizePhone(phone)}`;
}

export function whatsappLink(phone, message = "") {
  const p = phoneForWhatsapp(phone);
  const enc = encodeURIComponent(message || "");
  return `https://wa.me/${p}${enc ? `?text=${enc}` : ""}`;
}
// Normalize any phone-ish input into `+91XXXXXXXXXX` when it looks Indian.
export function normalizeIndianPhone(raw) {
  if (!raw) return "";
  const s = String(raw).trim();
  if (s.startsWith("+")) return "+" + s.replace(/[^\d]/g, "");
  const d = s.replace(/[^\d]/g, "");
  if (!d) return "";
  if (d.startsWith("0") && d.length === 11) return "+91" + d.slice(1);
  if (d.length === 10) return "+91" + d;
  if (d.startsWith("91") && d.length === 12) return "+" + d;
  if (d.length >= 10) return "+91" + d.slice(-10);
  return d;
}



// Best-effort extractor: given free-form text (typically pasted from a spreadsheet
// row or WhatsApp message), find a name, phone number and email.
// Never throws — returns "" for missing fields.
export function parseLeadText(raw) {
  const out = { name: "", phone: "", email: "" };
  if (!raw) return out;

  const text = String(raw).trim();
  if (!text) return out;

  // Strip common CRM/spreadsheet labels ("Name:", "Phone:", "Mobile #:", etc.)
  // BEFORE tokenising — otherwise "Name: Rahul" survives as one token and
  // ends up in the name field with the "Name:" prefix intact.
  const labelRx = /^(name|full ?name|customer|client|lead|phone|mobile|contact|number|whats?app|wa|email|e[-\s]?mail|mail)\s*(?:no\.?|number|#)?\s*[:\-–—]\s*/i;
  const stripLabel = (s) => {
    if (!s) return s;
    let cur = s.trim();
    // Some rows have chained labels, e.g. "Phone: Mobile: 98..."; loop.
    while (labelRx.test(cur)) cur = cur.replace(labelRx, "").trim();
    return cur;
  };

  // Email — always the most unambiguous match.
  const emailMatch = text.match(/[\w.+-]+@[\w-]+\.[\w.-]+/);
  if (emailMatch) out.email = emailMatch[0];

  // Split on tabs/newlines/2+ spaces FIRST so a spreadsheet-row paste
  // ('DATE\tPHONE\tNAME\t...') gives clean, per-cell tokens. We then classify
  // each token independently — this stops the phone regex from greedily
  // consuming the tab + date digits.
  const tokens = text
    .split(/\t+|\n+|\s{2,}/)
    .map((s) => s.trim())
    .map(stripLabel)
    .filter(Boolean);

  const dateRx = /^\d{1,4}[-/]\d{1,2}[-/]\d{1,4}$/;
  const phoneCharsRx = /^[+\d\-()\s]+$/;

  // Phone: first token that is entirely phone-like and has 7-15 digits.
  const phoneTokens = new Set();
  for (const t of tokens) {
    if (dateRx.test(t)) continue;
    if (out.email && t.includes(out.email)) continue;
    if (!phoneCharsRx.test(t)) continue;
    const digits = t.replace(/[^\d+]/g, "");
    const dcount = digits.replace(/\D/g, "").length;
    if (dcount < 7 || dcount > 15) continue;
    if (!out.phone) out.phone = normalizeIndianPhone(digits);
    phoneTokens.add(t);
  }
  // Fallback — scan the raw text for Indian mobile patterns (handles "+91 XXXXX XXXXX").
  if (!out.phone) {
    const rxs = [
      /\+?91[\s-]?\d{5}[\s-]?\d{5}/g,
      /\+?91[\s-]?\d{10}/g,
      /\b0\d{10}\b/g,
      /\b[6-9]\d{9}\b/g,
    ];
    for (const rx of rxs) {
      const m = text.match(rx);
      if (m && m[0]) { out.phone = normalizeIndianPhone(m[0]); break; }
    }
  }

  // Name: prefer 2-4-word letter-only tokens with no comma.
  const emailTokens = new Set(
    out.email ? tokens.filter((t) => t.includes(out.email)) : []
  );
  const nameCandidates = [];
  for (const t of tokens) {
    if (phoneTokens.has(t) || emailTokens.has(t)) continue;
    if (dateRx.test(t)) continue;
    if (/^\d/.test(t)) continue;
    if (t.includes(",")) continue; // likely a city/location
    const words = t.split(/\s+/);
    if (words.length > 4) continue;
    if (!/[A-Za-z]/.test(t)) continue;
    const letters = t.replace(/[^A-Za-z]/g, "").length;
    if (letters < 2) continue;
    if (letters / t.length < 0.6) continue;
    nameCandidates.push(t);
  }

  if (nameCandidates.length) {
    // Prefer 2-word (typical person name).
    nameCandidates.sort((a, b) => {
      const aw = a.split(/\s+/).length;
      const bw = b.split(/\s+/).length;
      return Math.abs(aw - 2) - Math.abs(bw - 2);
    });
    out.name = nameCandidates[0];
  }

  return out;
}

export function renderTemplate(body, lead) {
  return (body || "").replaceAll("{name}", lead?.name || "there");
}

export function initials(name) {
  return (name || "?")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}
